import React from 'react';
import Loader from '../../Components/common/loader/loader';
import { useAssetSummaryDocuments } from './useAssetSummaryDocuments';

type Props = {
  assetId?: string;
  selectedAssetId: string;
  selectedAssetRecordId: string;
  activeTab: string;
};

const AssetSummaryDocumentsSection: React.FC<Props> = ({
  assetId,
  selectedAssetId,
  selectedAssetRecordId,
  activeTab,
}) => {
  const docs = useAssetSummaryDocuments({ assetId, selectedAssetId, selectedAssetRecordId, activeTab });

  const documentsTab = (
    <div className="tab-content documents-content" style={{ display: activeTab === 'documents' ? 'block' : 'none' }}>
      <div className="documents-wrapper">
        <div className="documents-header">
          <h3 className="documents-title">Upload Documents</h3>
          <div className="upload-section">
            <button type="button" className="upload-button-blue" onClick={() => docs.setShowUploadModal(true)}>
              Upload
            </button>
          </div>
        </div>

        <div className="documents-grid">
          {docs.loadingDocuments ? (
            <div
              className="loading-container"
              style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}
            >
              <Loader />
            </div>
          ) : (
            docs.documents.map((doc) => (
              <div key={doc.id} className={`document-card ${docs.openDropdownId === doc.id ? 'dropdown-open' : ''}`}>
                <div className="document-icon-wrapper">
                  <svg className="document-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                      stroke="#60bfda"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path d="M14 2V8H20" stroke="#60bfda" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>

                <div className="doc-co">
                  <p className="document-title">{doc.name}</p>
                  <p className="document-date">{doc.date}</p>
                  <div className="document-dropdown-container">
                    <button
                      className="document-menu-btn"
                      onClick={(e) => docs.handleToggleDropdown(doc.id, e)}
                      title="More options"
                      type="button"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="5" r="1.5" fill="currentColor" />
                        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                        <circle cx="12" cy="19" r="1.5" fill="currentColor" />
                      </svg>
                    </button>

                    {docs.openDropdownId === doc.id && (
                      <div className="document-dropdown-menu">
                        <div className="document-dropdown-item" onClick={(e) => docs.handleDeleteFromDropdown(doc.id, e)}>
                          <span>Delete</span>
                        </div>
                        <div className="document-dropdown-item" onClick={(e) => docs.handleDownloadFromDropdown(doc.id, e)}>
                          <span>Download</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {documentsTab}
      {docs.showUploadModal && (
        <div className="upload-modal-overlay" onClick={docs.handleCloseModal}>
          <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h3 className="upload-modal-title">Upload document</h3>
              <button className="upload-modal-close-btn" onClick={docs.handleCloseModal} type="button" aria-label="Close">
                ×
              </button>
            </div>
            <div className="upload-modal-body">
              <div className="upload-modal-input-group">
                <label className="upload-modal-label">Document title</label>
                <input
                  type="text"
                  className="upload-modal-input"
                  placeholder="Enter Document Title"
                  value={docs.documentTitle}
                  onChange={(e) => docs.setDocumentTitle(e.target.value)}
                />
              </div>
              <div className="upload-modal-file-area">
                <div
                  className={`upload-modal-dropzone ${docs.isDragging ? 'dragging' : ''} ${docs.selectedFile ? 'has-file' : ''}`}
                  onDragOver={docs.handleDragOver}
                  onDragLeave={docs.handleDragLeave}
                  onDrop={docs.handleDrop}
                  onClick={() => document.getElementById('file-upload-input-modal')?.click()}
                >
                  <input
                    type="file"
                    id="file-upload-input-modal"
                    className="upload-modal-file-input"
                    onChange={docs.handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                  />
                  {docs.selectedFile ? (
                    <p className="upload-modal-file-name">{docs.selectedFile.name}</p>
                  ) : (
                    <>
                      <p className="upload-modal-file-text">Choose File To Upload</p>
                      <p className="upload-modal-file-hint">Or Drag and Drop Them Here</p>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="upload-modal-footer">
              <button type="button" className="upload-modal-save-button" onClick={docs.saveDocument}>
                Save
              </button>
              <button type="button" className="upload-modal-close-button" onClick={docs.handleCloseModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {docs.showDeleteModal && (
        <div className="delete-confirm-modal-overlay" onClick={docs.handleCloseDeleteModal}>
          <div className="delete-confirm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-modal-body">
              <p className="delete-confirm-message">Are you sure you want to delete this document?</p>
            </div>
            <div className="delete-confirm-modal-footer">
              <button type="button" className="delete-confirm-cancel-btn" onClick={docs.handleCloseDeleteModal}>
                Cancel
              </button>
              <button type="button" className="delete-confirm-ok-btn" onClick={docs.handleConfirmDelete}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {docs.showOverwriteModal && (
        <div className="delete-confirm-modal-overlay" onClick={docs.handleCancelOverwrite}>
          <div className="delete-confirm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-modal-body">
              <p className="delete-confirm-message">
                A document with the name "{docs.duplicateDocumentInfo?.name}" already exists. Do you want to overwrite it?
              </p>
            </div>
            <div className="delete-confirm-modal-footer">
              <button type="button" className="delete-confirm-cancel-btn" onClick={docs.handleCancelOverwrite}>
                No
              </button>
              <button type="button" className="delete-confirm-ok-btn" onClick={docs.handleConfirmOverwrite}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssetSummaryDocumentsSection;
